import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class AuthenticationService {
  async getPolicy(scopeType, scopeId) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/${scopeType}/${scopeId}`);

      if (!response.ok) {
        throw new Error(`Failed to load policy: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting policy:', error);
      throw error;
    }
  }

  async updatePasswordPolicy(scopeType, scopeId, policy) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/password/${scopeType}/${scopeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(policy)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to update password policy');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating password policy:', error);
      throw error;
    }
  }

  async updateMfaPolicy(scopeType, scopeId, policy) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/mfa/${scopeType}/${scopeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(policy)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to update MFA policy');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating MFA policy:', error);
      throw error;
    }
  }

  async updateSessionPolicy(scopeType, scopeId, policy) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/session/${scopeType}/${scopeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(policy)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to update session policy');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating session policy:', error);
      throw error;
    }
  }

  async updatePasswordAuthSetting(scopeType, scopeId, allowPasswordAuth) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/password-auth/${scopeType}/${scopeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ allowPasswordAuth })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to update password auth setting');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating password auth setting:', error);
      throw error;
    }
  }

  async getAppDefaults() {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/app-defaults`);

      if (!response.ok) {
        throw new Error(`Failed to load app defaults: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting app defaults:', error);
      throw error;
    }
  }

  async updateAppDefaultPasswordPolicy(policy) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/authentication-policies/app-defaults/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(policy)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to update app default password policy');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating app default password policy:', error);
      throw error;
    }
  }

  // SSO methods
  async getSsoConfigurations(scopeType, scopeId) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sso/${scopeType}/${scopeId}`);

      if (!response.ok) {
        throw new Error(`Failed to load SSO configurations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting SSO configurations:', error);
      throw error;
    }
  }

  async createSsoConfiguration(scopeType, scopeId, config) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sso/${scopeType}/${scopeId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to create SSO configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating SSO configuration:', error);
      throw error;
    }
  }

  async updateSsoConfiguration(configId, config) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sso/${configId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to update SSO configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating SSO configuration:', error);
      throw error;
    }
  }

  async deleteSsoConfiguration(configId) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sso/${configId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to delete SSO configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting SSO configuration:', error);
      throw error;
    }
  }

  async testSsoConfiguration(configId) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sso/${configId}/test`, {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to test SSO configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error testing SSO configuration:', error);
      throw error;
    }
  }

  // Session methods
  async getActiveSessions() {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sessions/active`);

      if (!response.ok) {
        throw new Error(`Failed to load sessions: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting active sessions:', error);
      throw error;
    }
  }

  async revokeSession(sessionId) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sessions/${sessionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to revoke session');
      }

      return await response.json();
    } catch (error) {
      console.error('Error revoking session:', error);
      throw error;
    }
  }

  async revokeAllSessions() {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/api/sessions/all`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Failed to revoke all sessions');
      }

      return await response.json();
    } catch (error) {
      console.error('Error revoking all sessions:', error);
      throw error;
    }
  }
}

const authenticationServiceInstance = new AuthenticationService();
export default authenticationServiceInstance;

